"use client";

import { Box, type BoxProps } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";

interface TypewriterTextProps extends BoxProps {
  words: readonly string[];
  typingMs?: number;
  deletingMs?: number;
  pauseMs?: number;
}

/**
 * Rotating typewriter text: types a word, pauses, deletes it, then moves to
 * the next. Dependency-free state machine, respects reduced-motion by
 * freezing on the first word.
 */
export const TypewriterText = ({
  words,
  typingMs = 110,
  deletingMs = 55,
  pauseMs = 1800,
  ...boxProps
}: TypewriterTextProps) => {
  const [wordIndex, setWordIndex] = useState(0);
  const [displayText, setDisplayText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const reducedMotionRef = useRef(false);

  useEffect(() => {
    reducedMotionRef.current = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (reducedMotionRef.current) {
      setDisplayText(words[0] ?? "");
    }
  }, [words]);

  useEffect(() => {
    if (reducedMotionRef.current) {
      return;
    }
    const fullWord = words[wordIndex % words.length] ?? "";
    let timer: ReturnType<typeof setTimeout>;

    if (!isDeleting && displayText === fullWord) {
      timer = setTimeout(() => setIsDeleting(true), pauseMs);
    } else if (isDeleting && displayText === "") {
      setIsDeleting(false);
      setWordIndex((i) => (i + 1) % words.length);
    } else {
      timer = setTimeout(
        () =>
          setDisplayText(
            isDeleting
              ? fullWord.slice(0, displayText.length - 1)
              : fullWord.slice(0, displayText.length + 1),
          ),
        isDeleting ? deletingMs : typingMs,
      );
    }

    return () => clearTimeout(timer);
  }, [displayText, isDeleting, wordIndex, words, typingMs, deletingMs, pauseMs]);

  return (
    <Box as="span" {...boxProps}>
      {displayText}
      <Box as="span" animation="pulse 1s ease-in-out infinite">
        |
      </Box>
    </Box>
  );
};
